import React, {useCallback, useEffect, useRef, useState} from 'react'
import {defineMessages, useIntl} from 'react-intl'

import {Button} from '@vio/atlas/Button'
import {Icon} from '@vio/atlas/Icon'
import useDebounce from '@vio/core/src/utils/debounce/hooks/useDebounce'

import {SearchFieldMobile} from './styles'

interface PropsType {
  displayValue?: string
  onSuggestionsRequested: (arg0: string, arg1: boolean) => void
  focusOnMount?: boolean
  shouldLoadUserSearchHistory?: boolean
  hasError?: boolean
  errorMessage?: string
}

const destinationPickerMessages = defineMessages({
  destinationOverlayPlaceholder: {
    id: 'whereAreYouGoing',
    defaultMessage: 'Where are you going?'
  }
})

export const DestinationPicker: React.FC<PropsType> = ({
  displayValue = '',
  onSuggestionsRequested,
  focusOnMount = false,
  shouldLoadUserSearchHistory = false
}) => {
  const {formatMessage} = useIntl()
  const inputFieldRef = useRef<null | HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState(displayValue)
  const debouncedInputValue: string = useDebounce(inputValue, 250)

  useEffect(() => {
    if (focusOnMount) inputFieldRef.current?.focus?.()
  }, [focusOnMount])

  useEffect(() => {
    setInputValue(displayValue)
  }, [setInputValue, displayValue])

  // Requesting suggestions can cause a loop when we rely on dep stability so check that value is actually changed
  const previousDebouncedInputValue = useRef<string>(debouncedInputValue)

  useEffect(() => {
    const isChanged =
      debouncedInputValue !== previousDebouncedInputValue.current
    if (!isChanged) return

    previousDebouncedInputValue.current = debouncedInputValue

    if (debouncedInputValue) {
      onSuggestionsRequested(debouncedInputValue, false)
    } else {
      onSuggestionsRequested('', true)
    }
  }, [debouncedInputValue, onSuggestionsRequested])

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value)
    },
    [setInputValue]
  )

  const handleInputFocus = useCallback(
    ({target}: React.ChangeEvent<HTMLInputElement>) => {
      const {value} = target
      // If the input has a value, request suggestions based on that value
      // If the input is empty, load user search history instead
      const loadUserHistory = value === '' || shouldLoadUserSearchHistory
      onSuggestionsRequested(value, loadUserHistory)
    },
    [shouldLoadUserSearchHistory, onSuggestionsRequested]
  )

  const handleClearDestination = useCallback(
    (event: React.SyntheticEvent) => {
      event.preventDefault()

      // Refocus first so the focus handler cannot overwrite the cleared value.
      inputFieldRef.current?.focus()
      setInputValue('')
    },
    [inputFieldRef, setInputValue]
  )

  return (
    <SearchFieldMobile
      ref={inputFieldRef}
      dataId="DestinationInput"
      id="DestinationInput"
      name="DestinationInput"
      customProps={{
        role: 'presentation',
        autoCapitalize: 'off',
        autoCorrect: 'off',
        spellCheck: 'false',
        onFocus: handleInputFocus
      }}
      prefix={
        <Icon name="Pin" size="md" colorPath="content.interactive.primary" />
      }
      placeholder={formatMessage(
        destinationPickerMessages.destinationOverlayPlaceholder
      )}
      value={inputValue}
      onChange={handleInputChange}
      suffix={
        inputValue && (
          <Button
            onClick={handleClearDestination}
            variant="ghost"
            size="sm"
            dataId="DestinationInputClear"
            iconStart={
              <Icon
                name="Clear"
                size="sm"
                colorPath="content.neutral.secondary"
              />
            }
          />
        )
      }
    />
  )
}
